/**
 * RegisterHandlers — SignalR → IndexedDB / sync profile.
 *
 * Liên quan PH/Version:
 * - onReloadOrgData: save*(raw, remoteVersion string) — save* tự wrap VersionDTO
 * - reloadEmployee / roleGroup: syncLoggedInUserPermissionsAfterRoleGroupChange (profile only)
 */
import { signalRService, SignalRNotificationMessage } from './SignalRService';
import { useRealtimeStore } from '@/store/RealtimeStore';
import {
  updateEmployeeInIndexedDB,
  deleteEmployeeFromIndexedDB,
  updateDepartmentInIndexedDB,
  deleteDepartmentFromIndexedDB,
  updatePositionInIndexedDB,
  deletePositionFromIndexedDB,
  updatePeriodInIndexedDB,
  deletePeriodFromIndexedDB,
  updateUnitInIndexedDB,
  deleteUnitFromIndexedDB,
  updateRoleGroupInIndexedDB,
  deleteRoleGroupFromIndexedDB,
  updateOrganizationVersion,
  syncLoggedInUserPermissionsAfterRoleGroupChange,
  saveRestToIndexedDB,
  saveEmployeesToIndexedDB,
  saveDepartmentsToIndexedDB,
  savePositionsToIndexedDB,
  getStoredOrgVersion,
  scheduleBatchSync,
  coalescingSync,
  startVersionPolling,
  checkVersionMismatch,
  dispatchOrganizationVersionUpdated,
  initOrgSyncBroadcastListener,
} from '@/utils/organizationIndexedDBUtils';
import { organizationApi } from '@/services/organization/organization-api';
import CryptoService from '@/utils/cryptoService';
import { setDefaultDepartment } from '@/components/Header/DropDownDepartment';
import CommonJs from '@/utils/commonJs';
import { handleDbRestoreSignal, logDbRestoreTrace } from '@/utils/dbRestoreSync';
import { logCopyDataTrace } from '@/utils/copyDataProgressSync';
import { parseCopyDataSignal } from '@/services/tenant/tenant-api-config';
import { useCopyDataProgressStore } from '@/store/copyDataProgressStore';
import { useAuthStore } from '@/store/useAuthStore';
import { saveLicenseToStorage } from '@/utils/licenseStorage';
import type { License } from '@/types/license';
import type { OrgVersion } from '@/types/organizationIndexedDB';
import * as signalR from '@microsoft/signalr';

/**
 * RegisterHandlers - Map SignalR events → Store actions
 * Tập trung tất cả event handler registration vào một chỗ duy nhất
 */

// Lưu trữ handlers để có thể remove sau
const handlers: Array<() => void> = [];
let isRegistered = false; // Flag để track xem đã đăng ký chưa

// 🔥 Flag để track reconnect listener đã được đăng ký chưa
let reconnectListenerRegistered = false;

// Lưu trữ reference của các handlers để tránh đăng ký trùng
let currentOnNotificationHandler: ((notification: SignalRNotificationMessage) => void) | null = null;

// Debounce để tránh xử lý notification trùng lặp
let lastNotificationTime = 0;
let lastNotificationId: string | null = null;
const NOTIFICATION_DEBOUNCE_MS = 100; // Chỉ xử lý notification mới sau 100ms

// Debounce cho reloadEmployee để tránh gọi API nhiều lần
let reloadEmployeeTimer: ReturnType<typeof setTimeout> | null = null;
const RELOAD_EMPLOYEE_DEBOUNCE_MS = 200;

// Debounce cho reloadRest để tránh gọi API nhiều lần
let reloadRestTimer: ReturnType<typeof setTimeout> | null = null;
const RELOAD_REST_DEBOUNCE_MS = 200;

// Debounce cho reloadDepartment để tránh gọi API nhiều lần
let reloadDepartmentTimer: ReturnType<typeof setTimeout> | null = null;
const RELOAD_DEPARTMENT_DEBOUNCE_MS = 200;

// Debounce cho reloadPosition để tránh gọi API nhiều lần
let reloadPositionTimer: ReturnType<typeof setTimeout> | null = null;
const RELOAD_POSITION_DEBOUNCE_MS = 200;

function resolveNotificationPayload(notification: SignalRNotificationMessage) {
  const n = notification as SignalRNotificationMessage & { Body?: string; Data?: Record<string, unknown> };
  const body = n.body ?? n.Body ?? '';
  const data = n.data ?? n.Data;
  return { body, data };
}

function resolveDeleteId(data: Record<string, unknown> | undefined): string | number | undefined {
  if (!data) return undefined;
  const id = data.id ?? data.ID ?? data.unitId ?? data.periodId;
  return id as string | number | undefined;
}

/**
 * 🔥 Đăng ký reconnect listener - CHỈ GỌI 1 LẦN khi app start
 * Listener này KHÔNG bị xóa khi unregister handlers
 */
export function initReconnectListener(): void {
  if (reconnectListenerRegistered) {
    return;
  }

  const handleReconnect = () => {

    // Kiểm tra xem có cần re-register không
    const authState = useAuthStore.getState();
    if (!authState.accessToken) {
      return;
    }

    // Start version polling as fallback
    startVersionPolling();

    // Kiểm tra version mismatch sau reconnect
    void checkVersionMismatch('employees');
    void checkVersionMismatch('departments');
    void checkVersionMismatch('positions');
    void checkVersionMismatch('rest');

    // Nếu handlers đã bị xóa (isRegistered = false), đăng ký lại
    if (!isRegistered) {
      registerRealtimeHandlers();
    } else {
      // Log handler counts để debug
    }
  };

  signalRService.onReconnect(handleReconnect);
  reconnectListenerRegistered = true;
}

/**
 * Đăng ký tất cả SignalR event handlers và map vào store
 * Chỉ gọi khi user đã authenticated (có accessToken)
 */
export function registerRealtimeHandlers(): void {
  // Kiểm tra authentication trước khi đăng ký
  const authState = useAuthStore.getState();
  if (!authState.accessToken) {
    console.warn('[Realtime] Cannot register handlers: No access token');
    return;
  }

  // Nếu đã đăng ký rồi, không đăng ký lại (tránh duplicate)
  if (isRegistered) {
    logDbRestoreTrace('1-skip-alreadyRegistered');
    return;
  }

  // Khởi tạo BroadcastChannel listener 1 lần (idempotent).
  // Lắng nghe `sync-complete` từ tab writer khác → patch UI in-memory.
  // Chạy sớm để không bỏ lỡ message đầu tiên sau login.
  initOrgSyncBroadcastListener();

  logDbRestoreTrace('1-FE-RegisterHandlers-start', {
    tenantID: authState.tenantID,
    profileId: authState.profile?.id,
  });

  const store = useRealtimeStore.getState();

  const validateSelectedDepartment = async () => {
    const { profile, tenantID } = useAuthStore.getState();
    if (!tenantID || !profile) return;

    const { usePrivateStore } = await import('@/store/usePrivateStore');
    const preferredSelectId = usePrivateStore.getState().getRequestHeader?.()?.selectTree?.idSelected;
    if (!preferredSelectId) {
      await setDefaultDepartment(profile, tenantID);
      return;
    }

    try {
      const { getOrganizationIndexedDB } = await import('@/utils/organizationIndexedDBUtils');
      const org = await getOrganizationIndexedDB();
      const allDeps = Array.isArray(org?.departments)
        ? org?.departments
        : Object.values(org?.departments || {});
      const isEmployeeSelected = CommonJs.checkIsEmployee(preferredSelectId);
      const depExists = isEmployeeSelected
        ? true
        : allDeps?.some((d: any) => String(d?.ID) === String(preferredSelectId));

      if (!depExists) {
        await setDefaultDepartment(profile, tenantID);
      }
    } catch {
      // ignore validate error
    }
  };

  // Employee & Department events
  const onReloadEmployee = (notification: SignalRNotificationMessage) => {
    const { body, data } = notification as any;
    const { profile } = useAuthStore.getState();

    try {
      if ((body === 'update' || body === 'create') && data) {
        // data dạng dictionary: { [id]: employeeRaw }
        Object.entries(data).forEach(([id, emp]: [string, any]) => {
          void updateEmployeeInIndexedDB(emp);

          // Kiểm tra user đang login có bị thay đổi không
          const profileId = profile?.id;
          if (profileId && (String(id) === String(profileId) || String(emp?.id) === String(profileId))) {
            // Kiểm tra xóa hoặc nghỉ việc
            const isDeleted = emp?.isDelete ?? emp?.isDeleted ?? emp?.IsDelete ?? emp?.IsDeleted;
            const isNghiViec = emp?.nghiViec ?? emp?.NghiViec ?? false;
            const trangThaiLamViec = emp?.trangThaiLamViec ?? emp?.TrangThaiLamViec;
            const isInactive = isDeleted || isNghiViec || trangThaiLamViec === 'Nghỉ việc' || trangThaiLamViec === 'Đã nghỉ';

            if (isInactive) {
              // User đang login bị xóa/nghỉ việc → force logout
              console.log('[Realtime] Logged-in user inactivated, forcing logout...', { id, emp });
              window.dispatchEvent(new CustomEvent('forceUserLogout', {
                detail: { reason: 'account_inactivated', message: 'Tài khoản của bạn đã bị vô hiệu hóa' }
              }));
            } else {
              // User đang login bị thay đổi role → sync permissions
              console.log('[Realtime] Logged-in user role changed, syncing permissions...', { id, emp });
              void syncLoggedInUserPermissionsAfterRoleGroupChange();
            }
          }
        });
      }
      else if (body === 'delete' && (data as any)?.id) {
        void deleteEmployeeFromIndexedDB((data as any).id);

        // Kiểm tra user đang login có bị xóa không
        const deletedId = String((data as any).id);
        const profileId = profile?.id;
        if (profileId && deletedId === String(profileId)) {
          console.log('[Realtime] Logged-in user deleted, forcing logout...', { deletedId });
          window.dispatchEvent(new CustomEvent('forceUserLogout', {
            detail: { reason: 'account_deleted', message: 'Tài khoản của bạn đã bị xóa' }
          }));
        }
      }
    } catch (err) {
      console.error('[Realtime] Error updating employees in IndexedDB:', err);
    }

    // Batch sync — gom N signals thành 1 API call, có coalescing
    scheduleBatchSync('employees');
    store.setInvalidate('employees', true);
    setTimeout(() => store.setInvalidate('employees', false), 3000);
  };
  signalRService.setOnReloadEmployee(onReloadEmployee);
  handlers.push(() => {
    signalRService.removeOnReloadEmployee(onReloadEmployee);
  });

  const onReloadDepartment = (notification: SignalRNotificationMessage) => {
    const { body, data } = notification as any;

    try {
      if ((body === 'update' || body === 'create') && data) {
        // data có thể là dictionary hoặc 1 object department
        const departments: any[] = Array.isArray(data)
          ? data
          : typeof data === 'object' && data !== null
            ? Object.values(data)
            : [];
        departments.forEach((dep) => {
          void updateDepartmentInIndexedDB(dep);
        });
      } else if (body === 'delete' && (data as any)?.id) {
        void deleteDepartmentFromIndexedDB((data as any).id);
      }
    } catch (err) {
      console.error('[Realtime] Error updating departments in IndexedDB:', err);
    }

    // Batch sync — gom N signals thành 1 API call, có coalescing
    scheduleBatchSync('departments');
    void validateSelectedDepartment();
    store.setInvalidate('departments', true);
    setTimeout(() => store.setInvalidate('departments', false), 3000);
  };
  signalRService.setOnReloadDepartment(onReloadDepartment);
  handlers.push(() => {
    signalRService.removeOnReloadDepartment(onReloadDepartment);
  });

  const onReloadRoleGroup = (notification: SignalRNotificationMessage) => {
    const { body, data, Body, Data } = notification as SignalRNotificationMessage & {
      Body?: string;
      Data?: Record<string, unknown>;
    };
    const action = body ?? Body;
    const payload = (data ?? Data) as Record<string, unknown> | undefined;

    try {
      if ((action === 'update' || action === 'create') && payload) {
        const roleGroups: unknown[] = Array.isArray(payload)
          ? payload
          : typeof payload === 'object' && payload !== null
            ? Object.values(payload)
            : [];
        roleGroups.forEach((rg) => {
          void updateRoleGroupInIndexedDB(rg);
        });
      } else if (action === 'delete' && payload?.id) {
        void deleteRoleGroupFromIndexedDB(payload.id as string | number);
      }
    } catch (err) {
      console.error('[Realtime] Error updating role groups in IndexedDB:', err);
    }

    void syncLoggedInUserPermissionsAfterRoleGroupChange();
    store.setInvalidate('rolegroups', true);
    setTimeout(() => store.setInvalidate('rolegroups', false), 3000);
  };
  signalRService.setOnReloadRoleGroup(onReloadRoleGroup);
  handlers.push(() => signalRService.removeOnReloadRoleGroup(onReloadRoleGroup));

  const onReloadPeriod = (notification: SignalRNotificationMessage) => {
    const { body, data } = resolveNotificationPayload(notification);
    console.log('[RegisterHandlers][reloadPeriod] received', { body, dataKeys: data ? Object.keys(data) : [] });

    try {
      if ((body === 'update' || body === 'create') && data) {
        const periods: any[] = Array.isArray(data)
          ? data
          : typeof data === 'object' && data !== null
            ? Object.values(data)
            : [];
        periods.forEach((period) => {
          void updatePeriodInIndexedDB(period);
        });
        console.log('[RegisterHandlers][reloadPeriod] IndexedDB patched', { count: periods.length, body });
      } else if (body === 'delete') {
        const deleteId = resolveDeleteId(data as Record<string, unknown>);
        if (deleteId != null) {
          void deletePeriodFromIndexedDB(deleteId);
          console.log('[RegisterHandlers][reloadPeriod] IndexedDB deleted', { id: deleteId });
        }
      }
    } catch (err) {
      console.error('[RegisterHandlers][reloadPeriod] IndexedDB error:', err);
    }
    console.log('[RegisterHandlers][reloadPeriod] setInvalidate(periods)');
    store.pulseInvalidate('periods');
  };
  signalRService.setOnReloadPeriod(onReloadPeriod);
  handlers.push(() => signalRService.removeOnReloadPeriod(onReloadPeriod));

  const onReloadUnit = async (notification: SignalRNotificationMessage) => {
    const { body, data } = resolveNotificationPayload(notification);
    console.log('[RegisterHandlers][reloadUnit] received', { body, dataKeys: data ? Object.keys(data) : [] });

    try {
      if ((body === 'update' || body === 'create') && data) {
        const units: any[] = Array.isArray(data)
          ? data
          : typeof data === 'object' && data !== null
            ? Object.values(data)
            : [];
        await Promise.all(units.map((unit) => updateUnitInIndexedDB(unit)));
        console.log('[RegisterHandlers][reloadUnit] IndexedDB patched', { count: units.length, body });
      } else if (body === 'delete') {
        const deleteId = resolveDeleteId(data as Record<string, unknown>);
        if (deleteId != null) {
          await deleteUnitFromIndexedDB(deleteId);
          console.log('[RegisterHandlers][reloadUnit] IndexedDB deleted', { id: deleteId });
        }
      }
    } catch (err) {
      console.error('[RegisterHandlers][reloadUnit] IndexedDB error:', err);
    }
    console.log('[RegisterHandlers][reloadUnit] setInvalidate(units)');
    store.pulseInvalidate('units');
  };
  signalRService.setOnReloadUnit(onReloadUnit);
  handlers.push(() => signalRService.removeOnReloadUnit(onReloadUnit));

  const onReloadPosition = (notification: SignalRNotificationMessage) => {
    const { body, data } = notification as any;
    // store.setNotificationData(notification)

    try {
      if ((body === 'update' || body === 'create') && data) {
        // data có thể là dictionary hoặc 1 object position
        const positions: any[] = Array.isArray(data)
          ? data
          : typeof data === 'object' && data !== null
            ? Object.values(data)
            : [];
        positions.forEach((pos) => {
          void updatePositionInIndexedDB(pos);
        });
      } else if (body === 'delete' && (data as any)?.id) {
        void deletePositionFromIndexedDB((data as any).id);
      }
    } catch (err) {
      console.error('[Realtime] Error updating positions in IndexedDB:', err);
    }

    // Batch sync — gom N signals thành 1 API call, có coalescing
    scheduleBatchSync('positions');
    store.setInvalidate('positions', true);
    setTimeout(() => store.setInvalidate('positions', false), 3000);
  };
  signalRService.setOnReloadPosition(onReloadPosition);
  handlers.push(() => {
    signalRService.removeOnReloadPosition(onReloadPosition);
  });

  // ========== Handler nhận SignalR notify version → FE tự gọi API lấy data cached từ Redis ==========
  /**
   * Backend chỉ notify {module, version, dataAvailable} (không gửi payload qua SignalR — tránh 413 nginx body size).
   * FE so sánh version local vs remote; nếu khác → gọi GET /cached-data/{module} → decryptAES → IndexedDB.
   * Disk cache đã bỏ — controller serve thẳng từ Redis (encrypted) với Cache-Control max-age=300.
   */
  const onReloadOrgData = async (notification: SignalRNotificationMessage) => {
    const n = notification as any;
    const data = n?.data ?? n?.Data ?? {};
    const remoteVersion = data?.version;
    const module = data?.module ?? 'rest';
    const dataAvailable = data?.dataAvailable;

    if (!dataAvailable || !remoteVersion) {
      // Backend không có cache sẵn → dùng batch sync fallback
      console.log('[RegisterHandlers][reloadOrgData] No cache available, using batch sync fallback', { module });
      scheduleBatchSync(module as any);
      return;
    }

    try {
      // So sánh version local trước khi gọi API — nếu khớp thì bỏ qua
      const localVersion = (await getStoredOrgVersion(module as any)) ?? null;
      if (localVersion && localVersion === remoteVersion) {
        console.log('[RegisterHandlers][reloadOrgData] version match, skip fetch', { module, remoteVersion });
        return;
      }

      // Gọi API lấy data cached từ Redis (BE encrypt AES trước khi return)
      const body: any = await organizationApi.getCachedData(module);

      if (!body?.success || !body?.data) {
        // Cache miss (TTL hết hoặc chưa fetch) → fallback batch sync
        console.warn('[RegisterHandlers][reloadOrgData] cache miss, fallback batch sync', { module });
        scheduleBatchSync(module as any);
        return;
      }

      // Decrypt AES → JSON.parse → raw object → IndexedDB
      // remoteVersion là string; save* tự wrap VersionDTO (tránh 400 khi POST lại).
      const raw = JSON.parse(CryptoService.decryptAES(body.data));
      console.log('[RegisterHandlers][reloadOrgData] Received cached data', { module, version: remoteVersion });

      switch (module) {
        case 'employees':
          await saveEmployeesToIndexedDB(raw, remoteVersion);
          break;
        case 'departments':
          await saveDepartmentsToIndexedDB(raw, remoteVersion);
          break;
        case 'positions':
          await savePositionsToIndexedDB(raw, remoteVersion);
          break;
        case 'rest':
          await saveRestToIndexedDB(raw, remoteVersion);
          break;
        default:
          console.warn('[RegisterHandlers][reloadOrgData] Unknown module, fallback batch sync', { module });
          scheduleBatchSync(module as any);
          return;
      }

      // Dispatch events để UI update
      dispatchOrganizationVersionUpdated(module as any);
      window.dispatchEvent(new CustomEvent('indexedDBReloaded', { detail: { module, source: 'signalr-notify', version: remoteVersion } }));

    } catch (err) {
      console.error('[RegisterHandlers][reloadOrgData] Error processing cached data:', err);
      // Fallback: gọi batch sync
      scheduleBatchSync(module as any);
    }
  };
  signalRService.setOnReloadOrgData?.(onReloadOrgData);
  handlers.push(() => {
    signalRService.removeOnReloadOrgData?.(onReloadOrgData);
  });

  const onReloadRest = (_notification: SignalRNotificationMessage) => {
    console.log('[RegisterHandlers][reloadRest] Syncing REST data...');

    // Debounce để tránh gọi API nhiều lần khi backend gửi nhiều events
    if (reloadRestTimer) {
      clearTimeout(reloadRestTimer);
    }
    reloadRestTimer = setTimeout(async () => {
      reloadRestTimer = null;
      try {
        // Dùng batch sync thay vì gọi trực tiếp
        // scheduleBatchSync đã có debounce 300ms + coalescing
        scheduleBatchSync('rest');
      } catch (error) {
        console.error('[RegisterHandlers][reloadRest] Error:', error);
      }
    }, RELOAD_REST_DEBOUNCE_MS);
  };
  signalRService.setOnReloadRest(onReloadRest);
  handlers.push(() => {
    if (reloadRestTimer) {
      clearTimeout(reloadRestTimer);
      reloadRestTimer = null;
    }
    signalRService.removeOnReloadRest(onReloadRest);
  });

  const onReloadDbRestore = (notification: SignalRNotificationMessage) => {
    const n = notification as SignalRNotificationMessage & { body?: string; Body?: string; data?: Record<string, unknown>; Data?: Record<string, unknown> };
    const body = String(n.body ?? n.Body ?? '').toLowerCase();
    const dataPhase = String(n.data?.phase ?? n.Data?.phase ?? n.data?.Phase ?? n.Data?.Phase ?? '').toLowerCase();
    logDbRestoreTrace(body === 'complete' || dataPhase === 'complete'
      ? '11-FE-RegisterHandlers-onReloadDbRestore-COMPLETE'
      : '11-FE-RegisterHandlers-onReloadDbRestore', {
      body,
      dataPhase,
      notification,
    });
    handleDbRestoreSignal(notification as unknown as Record<string, unknown>);
  };
  signalRService.setOnReloadDbRestore(onReloadDbRestore);
  handlers.push(() => signalRService.removeOnReloadDbRestore(onReloadDbRestore));

  const onReloadCopyData = (notification: SignalRNotificationMessage) => {
    const signal = parseCopyDataSignal(notification as unknown as Record<string, unknown>);
    logCopyDataTrace('RegisterHandlers-onReloadCopyData', { signal, notification });
    if (!signal) return;
    useCopyDataProgressStore.getState().applySignal(signal);
  };
  signalRService.setOnReloadCopyData(onReloadCopyData);
  handlers.push(() => signalRService.removeOnReloadCopyData(onReloadCopyData));

  // License reload handler - cập nhật license khi admin thay đổi
  const onReloadLicense = (notification: SignalRNotificationMessage) => {
    const n = notification as SignalRNotificationMessage & { data?: Record<string, unknown>; Data?: Record<string, unknown> };
    const data = n.data ?? n.Data;
    console.log('[RegisterHandlers][reloadLicense] received', { data });

    if (data?.license) {
      const tenantID = useAuthStore.getState().tenantID;
      if (tenantID) {
        saveLicenseToStorage(tenantID, data.license as License);
        console.log('[RegisterHandlers][reloadLicense] license updated in storage', { tenantID });
      }
    }

    // Trigger license invalidate flag
    store.setInvalidate('license', true);
    setTimeout(() => store.setInvalidate('license', false), 3000);
  };
  signalRService.setOnReloadLicense(onReloadLicense);
  handlers.push(() => signalRService.removeOnReloadLicense(onReloadLicense));

    // Config reload handler - xóa cache và preload lại config khi admin thay đổi
    const onReloadConfig = async (notification: SignalRNotificationMessage) => {
      const n = notification as SignalRNotificationMessage & { data?: Record<string, unknown>; Data?: Record<string, unknown> };
      const data = n.data ?? n.Data;
      console.log('[RegisterHandlers][reloadConfig] received', { data });

      // Invalidate cache để buộc reload
      const { invalidateConfigKeysCache } = await import('@/services/config/config-keys-store');
      const tenantID = useAuthStore.getState().tenantID;
      invalidateConfigKeysCache(tenantID);

      // Preload lại config mới
      const { getConfigKeys } = await import('@/services/config/config-keys-store');
      getConfigKeys(true, tenantID).catch((err) => {
        console.warn('[Config] Reload failed:', err);
      });

      // Trigger config invalidate flag
      store.setInvalidate('config', true);
      setTimeout(() => store.setInvalidate('config', false), 3000);
    };
  signalRService.setOnReloadConfig(onReloadConfig);
  handlers.push(() => signalRService.removeOnReloadConfig(onReloadConfig));

  logDbRestoreTrace('1-FE-RegisterHandlers-done', {
    handlerCount: 1,
    tenantID: useAuthStore.getState().tenantID,
    profileId: useAuthStore.getState().profile?.id,
  });

  // Thread events
  const onThreadCreated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threads', true);
    setTimeout(() => store.setInvalidate('threads', false), 3000);
  };
  signalRService.setOnThreadCreated(onThreadCreated);
  handlers.push(() => signalRService.removeOnThreadCreated(onThreadCreated));

  const onThreadUpdated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threads', true);
    setTimeout(() => store.setInvalidate('threads', false), 3000);
  };
  signalRService.setOnThreadUpdated(onThreadUpdated);
  handlers.push(() => signalRService.removeOnThreadUpdated(onThreadUpdated));

  const onThreadDeleted = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threads', true);
    setTimeout(() => store.setInvalidate('threads', false), 3000);
  };
  signalRService.setOnThreadDeleted(onThreadDeleted);
  handlers.push(() => signalRService.removeOnThreadDeleted(onThreadDeleted));

  const onThreadReactionUpdated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadReactions', true);
    setTimeout(() => store.setInvalidate('threadReactions', false), 3000);
  };
  signalRService.setOnThreadReactionUpdated(onThreadReactionUpdated);
  handlers.push(() => signalRService.removeOnThreadReactionUpdated(onThreadReactionUpdated));

  // Comment events
  const onCommentCreated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('comments', true);
    setTimeout(() => store.setInvalidate('comments', false), 3000);
  };
  signalRService.setOnCommentCreated(onCommentCreated);
  handlers.push(() => signalRService.removeOnCommentCreated(onCommentCreated));

  const onCommentUpdated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('comments', true);
    setTimeout(() => store.setInvalidate('comments', false), 3000);
  };
  signalRService.setOnCommentUpdated(onCommentUpdated);
  handlers.push(() => signalRService.removeOnCommentUpdated(onCommentUpdated));

  const onCommentDeleted = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('comments', true);
    setTimeout(() => store.setInvalidate('comments', false), 3000);
  };
  signalRService.setOnCommentDeleted(onCommentDeleted);
  handlers.push(() => signalRService.removeOnCommentDeleted(onCommentDeleted));

  const onCommentReactionUpdated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('commentReactions', true);
    setTimeout(() => store.setInvalidate('commentReactions', false), 3000);
  };
  signalRService.setOnCommentReactionUpdated(onCommentReactionUpdated);
  handlers.push(() => signalRService.removeOnCommentReactionUpdated(onCommentReactionUpdated));

  // ThreadPage events
  const onThreadPageCreated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPages', true);
    setTimeout(() => store.setInvalidate('threadPages', false), 3000);
  };
  signalRService.setOnThreadPageCreated(onThreadPageCreated);
  handlers.push(() => signalRService.removeOnThreadPageCreated(onThreadPageCreated));

  const onThreadPageUpdated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPages', true);
    setTimeout(() => store.setInvalidate('threadPages', false), 3000);
  };
  signalRService.setOnThreadPageUpdated(onThreadPageUpdated);
  handlers.push(() => signalRService.removeOnThreadPageUpdated(onThreadPageUpdated));

  const onThreadPageDeleted = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPages', true);
    setTimeout(() => store.setInvalidate('threadPages', false), 3000);
  };
  signalRService.setOnThreadPageDeleted(onThreadPageDeleted);
  handlers.push(() => signalRService.removeOnThreadPageDeleted(onThreadPageDeleted));

  const onThreadPageUserJoined = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPageUsers', true);
    setTimeout(() => store.setInvalidate('threadPageUsers', false), 3000);
  };
  signalRService.setOnThreadPageUserJoined(onThreadPageUserJoined);
  handlers.push(() => signalRService.removeOnThreadPageUserJoined(onThreadPageUserJoined));

  const onThreadPageUserLeft = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPageUsers', true);
    setTimeout(() => store.setInvalidate('threadPageUsers', false), 3000);
  };
  signalRService.setOnThreadPageUserLeft(onThreadPageUserLeft);
  handlers.push(() => signalRService.removeOnThreadPageUserLeft(onThreadPageUserLeft));

  const onThreadPageRequestApproved = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPageRequests', true);
    setTimeout(() => store.setInvalidate('threadPageRequests', false), 3000);
  };
  signalRService.setOnThreadPageRequestApproved(onThreadPageRequestApproved);
  handlers.push(() => signalRService.removeOnThreadPageRequestApproved(onThreadPageRequestApproved));

  const onThreadPageRequestRejected = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPageRequests', true);
    setTimeout(() => store.setInvalidate('threadPageRequests', false), 3000);
  };
  signalRService.setOnThreadPageRequestRejected(onThreadPageRequestRejected);
  handlers.push(() => signalRService.removeOnThreadPageRequestRejected(onThreadPageRequestRejected));

  const onThreadPageOwnershipTransferred = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPageOwnership', true);
    setTimeout(() => store.setInvalidate('threadPageOwnership', false), 3000);
  };
  signalRService.setOnThreadPageOwnershipTransferred(onThreadPageOwnershipTransferred);
  handlers.push(() => signalRService.removeOnThreadPageOwnershipTransferred(onThreadPageOwnershipTransferred));

  // ThreadPoll events
  const onThreadPollCreated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPolls', true);
    setTimeout(() => store.setInvalidate('threadPolls', false), 3000);
  };
  signalRService.setOnThreadPollCreated(onThreadPollCreated);
  handlers.push(() => signalRService.removeOnThreadPollCreated(onThreadPollCreated));

  const onThreadPollUpdated = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPolls', true);
    setTimeout(() => store.setInvalidate('threadPolls', false), 3000);
  };
  signalRService.setOnThreadPollUpdated(onThreadPollUpdated);
  handlers.push(() => signalRService.removeOnThreadPollUpdated(onThreadPollUpdated));

  const onThreadPollDeleted = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPolls', true);
    setTimeout(() => store.setInvalidate('threadPolls', false), 3000);
  };
  signalRService.setOnThreadPollDeleted(onThreadPollDeleted);
  handlers.push(() => signalRService.removeOnThreadPollDeleted(onThreadPollDeleted));

  const onThreadPollVoted = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('threadPollVotes', true);
    setTimeout(() => store.setInvalidate('threadPollVotes', false), 3000);
  };
  signalRService.setOnThreadPollVoted(onThreadPollVoted);
  handlers.push(() => signalRService.removeOnThreadPollVoted(onThreadPollVoted));

  const onReloadQuizIframe = (notification: SignalRNotificationMessage) => {
    store.setNotificationData(notification);
    store.setInvalidate('reloadQuizIframe', true);
    setTimeout(() => store.setInvalidate('reloadQuizIframe', false), 3000);
  };
  signalRService.setOnReloadQuizIframe(onReloadQuizIframe);
  handlers.push(() => signalRService.removeOnReloadQuizIframe(onReloadQuizIframe));

  // Generic notification handler
  // Xóa handler cũ nếu đã tồn tại để tránh đăng ký trùng
  if (currentOnNotificationHandler) {
    signalRService.removeOnNotification(currentOnNotificationHandler);
  }

  const onNotification = (notification: SignalRNotificationMessage) => {

    const now = Date.now();
    // Tạo unique ID từ các thuộc tính của notification để detect duplicate
    const notificationId = `${notification.type || ''}_${notification.title || ''}_${
      notification.timestamp || ''
    }_${JSON.stringify(notification.data || {})}`;

    // Debounce: Bỏ qua notification trùng lặp trong khoảng thời gian ngắn
    if (notificationId === lastNotificationId && now - lastNotificationTime < NOTIFICATION_DEBOUNCE_MS) {
      return;
    }

    lastNotificationTime = now;
    lastNotificationId = notificationId;

    store.setNotificationData(notification);
    // Set generic invalidate flag
    store.setInvalidate('notifications', true);
    setTimeout(() => store.setInvalidate('notifications', false), 3000);
    // Có thể parse notification.type để set specific flags
    if (notification.type) {
      const type = notification.type.toLowerCase();
      if (type.includes('employee')) {
        store.setInvalidate('employees', true);
        setTimeout(() => store.setInvalidate('employees', false), 3000);
      }
      if (type.includes('department')) {
        store.setInvalidate('departments', true);
        setTimeout(() => store.setInvalidate('departments', false), 3000);
      }
      if (type.includes('rolegroup')) {
        store.setInvalidate('rolegroups', true);
        setTimeout(() => store.setInvalidate('rolegroups', false), 3000);
      }
    }
  };

  // Lưu reference để có thể xóa sau
  currentOnNotificationHandler = onNotification;

  signalRService.setOnNotification(onNotification);
  handlers.push(() => {
    signalRService.removeOnNotification(onNotification);
    if (currentOnNotificationHandler === onNotification) {
      currentOnNotificationHandler = null;
    }
  });

  // Register connection state change handler
  const handleConnectionStateChanged = (isConnected: boolean) => {
    const store = useRealtimeStore.getState();
    const connectionState = signalRService.getConnectionState();
    const connectionId = signalRService.getConnectionId();

    store.setConnectionState(isConnected, connectionState, connectionId);

    // Log connection state change
    const stateText =
      connectionState === signalR.HubConnectionState.Connected
        ? 'Connected'
        : connectionState === signalR.HubConnectionState.Connecting
        ? 'Connecting'
        : connectionState === signalR.HubConnectionState.Reconnecting
        ? 'Reconnecting'
        : connectionState === signalR.HubConnectionState.Disconnecting
        ? 'Disconnecting'
        : 'Disconnected';

  };

  signalRService.setOnConnectionStateChanged(handleConnectionStateChanged);
  handlers.push(() => signalRService.removeOnConnectionStateChanged(handleConnectionStateChanged));

  // Initial connection state update
  const initialState = signalRService.getConnectionState();
  const initialId = signalRService.getConnectionId();
  const initialIsConnected = signalRService.isConnectionActive();
  store.setConnectionState(initialIsConnected, initialState, initialId);

  // Đánh dấu đã đăng ký
  isRegistered = true;
}

/**
 * Hủy đăng ký tất cả handlers (cleanup) - GỌI KHI LOGOUT
 */
export function unregisterRealtimeHandlers(): void {
  // Nếu chưa đăng ký, không cần cleanup
  if (!isRegistered) {
    return;
  }

  // 🔥 XÓA THẬT SỰ handlers khỏi SignalRService Sets
  handlers.forEach((removeHandler) => {
    try {
      removeHandler();
    } catch (error) {
      console.error('[Realtime] Error removing handler:', error);
    }
  });

  // Clear handlers array
  handlers.length = 0;

  // Reset flag
  isRegistered = false;

  // Clear handler references
  currentOnNotificationHandler = null;
  lastNotificationTime = 0;
  lastNotificationId = null;

  // Reset connection state in store
  const store = useRealtimeStore.getState();
  store.setConnectionState(false, null, null);

}

/**
 * Kiểm tra xem handlers đã được đăng ký chưa
 */
export function isHandlersRegistered(): boolean {
  return isRegistered;
}

/**
 * Force re-register handlers (dùng khi cần reset)
 */
export function forceReregisterHandlers(): void {
  unregisterRealtimeHandlers();
  registerRealtimeHandlers();
}
